import React, { useContext, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { ClientDto } from 'api/Clients/Types/Client'
import { TradingAccountDto, TradingAccountStatus } from 'api/Clients/Types/TradingAccount'
import { AccountGroupType } from 'api/Trading-Accounts/Types/AccountGroupType'
import { PlatformTypeId } from 'api/Trading-Accounts/Types/PlatformTypeDto'
import { TransactionDto } from 'api/Transaction/Types/Transaction'
import { CurrencyType, WalletType } from 'api/Wallet/Types/Wallet'
import { Loading } from 'components/Loading'
import { ToastContext } from 'components/Toast/Toast'
import { Operator } from 'helpers/generic/search'
import { getIdFromLocationHeader } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { ExchangeRatesApiClient } from 'utils/api/exchangeRatesApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'

import ConversionForm, {
  ConversionDirection,
  CreateConversionFormSubmitValues,
  PresetAccount,
} from './ConversionForm'

const useConversionFetch = (client: ClientDto | null) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const { callback, forceRefresh } = useCallbackWithForceRefresh(async () => {
    if (!client) {
      return
    }
    return Promise.all([
      tradingAccountsApiClient.getTradingAccountsWithPlatformOverview({
        pageSize: 150,
        search: {
          Client: client?.id,
          AccountGroupType: {
            value: AccountGroupType.Live,
            operator: Operator.EQUAL,
          },
          Platform: {
            value: PlatformTypeId.CQGCast, // it's for ETD
            operator: Operator.EQUAL,
          },
          Status: {
            value: TradingAccountStatus.Approved,
            operator: Operator.EQUAL,
          },
        },
      }),
      tradingAccountsApiClient.getCurrenciesByPlatformType(PlatformTypeId.CQGCast),
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client])
  const { data, isLoading } = useFetchOne(callback)
  const [tradingAccounts, availableTACurrencies = []] = data || []

  return {
    tradingAccounts: tradingAccounts?.items || [],
    availableTACurrencies,
    isLoading,
    forceRefresh,
  }
}

interface Props {
  client: ClientDto | null
  preselectedTradingAccount?: TradingAccountDto | undefined
  setClient(): void
}

export const ConversionPage: React.FC<Props> = (props) => {
  const [submitting, setSubmitting] = useState(false)
  const walletsApiClient = useApiClient(WalletsApiClient)
  const exchangeRateApiClient = useApiClient(ExchangeRatesApiClient)
  const setToast = useContext(ToastContext)

  const { preselectedTradingAccount, client, setClient } = props

  const [currentRate, setCurrentRate] = useState(0)
  const [rateLoading, setRateLoadng] = useState(false)

  const { tradingAccounts, availableTACurrencies, isLoading, forceRefresh } =
    useConversionFetch(client)

  const getPresetAccount = (): PresetAccount | undefined => {
    if (preselectedTradingAccount && tradingAccounts.length) {
      const account = tradingAccounts.find((ta) => ta.platformOverview.balances.length)
      if (account) {
        return {
          account,
          currency: preselectedTradingAccount?.currency?.id || account?.currency?.id,
        }
      }
    }
  }

  const onSubmit = async (values: CreateConversionFormSubmitValues) => {
    try {
      setSubmitting(true)
      let response: TransactionDto | null = null

      const isFrom = values.direction === ConversionDirection.From
      const conversionDirection = {
        [isFrom ? 'fromAmount' : 'toAmount']: isFrom ? values.fromAmount : values.toAmount,
      }

      if (values.fromCurrencyId && values.toCurrencyId) {
        response = await walletsApiClient.convertTradingAccountBalance({
          fromAmount: values.fromAmount,
          toAmount: values.toAmount,
          tradingAccountId: values.fromId || values.toId,
          fromCurrencyId: values.fromCurrencyId,
          toCurrencyId: values.toCurrencyId,
          comment: values.comment,
          overrideFreeMarginCheck: true,
          ...conversionDirection,
        })
      }

      if (response) {
        setToast({
          type: 'success',
          requireInteraction: true,
          title: 'Transaction has been submitted.',
          subtitle: 'Click for details',
          url: generatePath('/transactions/:id', {
            id: getIdFromLocationHeader(response.id),
          }),
        })
      }

      setTimeout(() => {
        forceRefresh()
        setSubmitting(false)
      }, 6000)
    } catch (error: unknown) {
      console.error(error)
    }
  }

  const getRate = async (BaseCcy: CurrencyType, QuoteCcy: CurrencyType) => {
    try {
      setRateLoadng(true)
      const { rate } = await exchangeRateApiClient.getLatestExchangeRate(
        QuoteCcy,
        BaseCcy,
        'sell',
        WalletType.ETD,
        WalletType.ETD
      )
      setCurrentRate(rate)
    } finally {
      setRateLoadng(false)
    }
  }

  return (
    <Loading isLoading={isLoading || submitting}>
      <ConversionForm
        rateLoading={rateLoading}
        onSubmit={onSubmit}
        fetchRate={getRate}
        exchangeRate={currentRate}
        tradingAccounts={tradingAccounts}
        availableTACurrencies={availableTACurrencies}
        fromAccount={getPresetAccount()}
        setClient={setClient}
        client={client}
      />
    </Loading>
  )
}
